import { useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Telescope, Mail, Lock, ArrowRight, UserPlus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function Signup() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", { email, password });
      localStorage.setItem("astrolift_token", data.token);
      toast.success("Compte créé. Bienvenue !");
      window.location.href = "/";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative grid lg:grid-cols-2">
      <div className="starfield" aria-hidden />
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-white/5">
        <div className="absolute -top-32 -left-20 w-[36rem] h-[36rem] aurora rounded-full" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/40 to-blue-500/10">
            <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
          </div>
          <div className="font-medium tracking-tight">AstroLift</div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="label-eyebrow mb-4">Créer un compte</div>
          <h1 className="text-5xl font-light tracking-tighter leading-[1.05]">
            Rejoignez l'observatoire <span className="text-violet-300">numérique</span>.
          </h1>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            5 modes de traitement, classification automatique, médiathèque privée, exports
            JPG/PNG/TIFF. Vos images restent votre propriété.
          </p>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground/70">
          AstroLift n'invente rien. Aucune génération artificielle.
        </div>
      </div>

      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/30 to-blue-500/10">
              <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
            </div>
            <div className="font-medium tracking-tight">AstroLift</div>
          </div>
          <div className="label-eyebrow mb-3">Inscription</div>
          <h2 className="text-3xl font-light tracking-tight mb-2">Créer votre compte</h2>
          <p className="text-sm text-muted-foreground mb-8">Quelques secondes suffisent.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email-input"
                  className="w-full bg-secondary/40 border border-border rounded-md py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="vous@exemple.com" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="signup-password-input"
                  className="w-full bg-secondary/40 border border-border rounded-md py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="8 caractères minimum" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Confirmation</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} data-testid="signup-confirm-input"
                  className="w-full bg-secondary/40 border border-border rounded-md py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="••••••••" />
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="signup-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-medium hover:bg-primary/90 transition-all glow-violet disabled:opacity-60">
              {busy ? "Création…" : "Créer mon compte"} {!busy && <UserPlus className="w-4 h-4" />}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-sm text-muted-foreground">Déjà un compte ? </span>
            <Link to="/login" className="text-sm text-violet-300 hover:underline" data-testid="link-to-login">Se connecter</Link>
          </div>

          <p className="mt-10 text-[11px] text-muted-foreground/70 tracking-wide">
            Hash bcrypt · JWT · Données privées
          </p>
        </div>
        <Toaster theme="dark" />
      </div>
    </div>
  );
}
